// Enums for better type safety
export enum DemandStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum DemandCategory {
  MAINTENANCE = 'maintenance',
  CLEANING = 'cleaning',
  SECURITY = 'security',
  INFRASTRUCTURE = 'infrastructure',
  COMMON_AREAS = 'common_areas',
  OTHER = 'other'
}

// Aliases para compatibilidade com código existente
export const DemandCategoryAliases = {
  manutencao: DemandCategory.MAINTENANCE,
  limpeza: DemandCategory.CLEANING,
  seguranca: DemandCategory.SECURITY,
  infraestrutura: DemandCategory.INFRASTRUCTURE,
  areas_comuns: DemandCategory.COMMON_AREAS,
  outros: DemandCategory.OTHER
} as const;

export enum DemandPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent'
}

export enum UserRole {
  ADMIN = 'admin',
  MANAGER = 'manager',
  USER = 'user'
}

// Removido userRole - usando UserRole para todos os usuários

// Branded types for IDs to prevent mixing
export type UserId = string & { readonly __brand: 'UserId' };
export const toUserId = (id: string): UserId => id as UserId;
export type ClientId = string & { readonly __brand: 'ClientId' };
export type DemandId = string & { readonly __brand: 'DemandId' };
// Removido userId - usando UserId para todos os usuários

export interface IDemand {
  _id: DemandId;
  demandNumber: string;
  requesterName: string;
  requesterPhone: string;
  wantsWhatsappUpdates: boolean;
  wantsEmailUpdates?: boolean; // Adicionado para verificar se solicitante quer receber emails
  block?: string;
  unitNumber?: string;
  applicantType?: string; // Tipo de solicitante (ex.: morador, zelador). Formulário público via QR code.
  category: DemandCategory;
  description: string;
  images: string[];
  status: DemandStatus;
  priority: DemandPriority;
  /** @deprecated nunca populado pela API (o backend envia `clientId`, minúsculo) — mantido só por
   * compatibilidade de tipo com código legado; não usar em código novo. */
  ClientId: ClientId;
  /** Id do condomínio desta demanda, como a API realmente envia (minúsculo) — usar este campo,
   * não `ClientId` (ver nota acima). Vem como objeto populado (`{_id, name, address, ...}`, sem
   * `profileImage`) nas rotas que agrupam por cliente (board); normalizar com
   * getClientIdString antes de usar como id puro. */
  clientId?: string | { _id?: string; id?: string; name?: string; address?: string } | null;
  assigneeId?: UserId;
  /** Prestador para quem a demanda foi encaminhada (ProviderForwardDialog) — independente de
   * assigneeId (usuário interno); os dois podem coexistir. Só o id vem da API, sem populate. */
  providerId?: string;
  recurringDemandId?: string; // ID da recorrência que gerou esta demanda
  qrCodeId?: string; // ID do QR code usado para criar esta demanda
  /** true = vínculo manual feito por um gestor; ausente/false = originado do formulário público
   * disparado por escaneamento do QR. Indicador visual no card do board (Fase 5). */
  qrCodeLinkedByAdmin?: boolean;
  /** Checklist/lista de patrimônio operacionais opcionais — Fases 2 e 4, ver
   * PLANO_CHECKLIST_QRCODE_PATRIMONIO. */
  checklistId?: string;
  patrimonyListId?: string;
  /** Preenchidos quando esta demanda nasceu de um item de patrimônio danificado (spec item 13). */
  originPatrimonyInspectionId?: string;
  originPatrimonyItemName?: string;
  /** Identificação do item informada no formulário público (ex.: número do poste), quando o QR exige */
  itemIdentification?: string;
  mergedDemands?: string[]; // IDs das demandas unificadas nesta (se esta é a principal)
  estimatedCompletion?: Date;
  /** Preenchida automaticamente na 1ª vez que a demanda entra em "em andamento". Ausente em
   * demandas concluídas antes desta feature existir. */
  startedAt?: Date;
  completedAt?: Date;
  cancelledAt?: Date;
  /** Nome informado por quem executou o serviço, ao concluir via código de prestador no QR público. */
  completedByProviderName?: string;
  /** @deprecated substituído por `observations` — mantido só por compatibilidade de dados antigos. */
  notes?: string;
  /** Lista de observações da demanda, mais recente primeiro (ordenar por `createdAt` ao exibir). */
  observations?: IDemandObservation[];
  whatsappSent: boolean;
  emailSent: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface IDemandObservation {
  _id: string;
  text: string;
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  updatedAt?: string;
  updatedBy?: string;
  updatedByName?: string;
}

export interface IClient {
  _id: ClientId;
  name: string;
  address: string;
  number?: string;
  neighborhood?: string;
  city: string;
  state: string;
  zipCode: string;
  cnpj: string;
  unitsCount: number;
  hasBlocks?: boolean;
  blocks?: Array<{ identifier: string; unitStart: number; unitEnd: number }>;
  paymentDay?: number; // Dia de vencimento do pagamento (1-31)
  profileImage?: string;
  isActive: boolean;
  /** Painel Público da Operação (Relatório B — "Desempenho & Transparência"), ver
   * ClientPublicReportConfig.tsx. Desligado por padrão; token só existe depois da 1ª ativação. */
  publicReportEnabled?: boolean;
  publicReportToken?: string;
  /** Mensagem de conscientização no topo do painel público (pedido do usuário, 10/09) — semeada
   * com o texto padrão na 1ª ativação, editável pela página /public-panel. */
  publicReportWelcomeMessage?: string;
  deletedAt?: Date | string | null; // Soft delete field
  createdAt: Date;
  updatedAt: Date;
}

// Removido Iuser - funcionalidades integradas ao IUser

export interface IKanbanColumn {
  pending: IDemand[];
  in_progress: IDemand[];
  completed: IDemand[];
  cancelled: IDemand[];
}

export interface IDemandsData {
  client: {
    id: ClientId;
    name: string;
    address: string;
    profileImage?: string;
  };
  demands: IKanbanColumn;
  totalDemands: number;
}

export interface IDemandsStats {
  totalDemands: number;
  openDemands: number;
  inProgressDemands: number;
  completedDemands: number;
  cancelledDemands: number;
  avgResolutionTime: number;
  totalClients: number;
  activeUsers: number;
}

export interface IReportsStats {
  totalDemands: number;
  openDemands: number;
  inProgressDemands: number;
  completedDemands: number;
  cancelledDemands: number;
  avgResolutionTime: number;
}

export interface IApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Alias para compatibilidade
export type ApiResponse<T = unknown> = IApiResponse<T>;

export interface IDemandUpdateData {
  status?: DemandStatus | string;
  priority?: DemandPriority | string;
  category?: string;
  assigneeId?: string | null;
  /** null desatribui o prestador (limpa também providerNotifiedAt/Channel e completionToken no
   * backend) — usado quando "Nenhum" é escolhido no encaminhamento (ProviderForwardDialog). */
  providerId?: string | null;
  estimatedCompletion?: string | null;
  notes?: string | null;
  completedAt?: string | null;
  /** Texto de observação inserido junto com uma troca de status (coluna configurada para exigir). */
  observationText?: string;
  /** QR code/checklist/lista de patrimônio opcionais — Fase 5, ver PLANO_CHECKLIST_QRCODE_PATRIMONIO.
   * null desvincula. Editáveis mesmo numa ocorrência gerada por recorrência (ver
   * UpdateRecurringDemandDialog para o fluxo de escolha de escopo). */
  qrCodeId?: string | null | undefined;
  checklistId?: string | null | undefined;
  patrimonyListId?: string | null | undefined;
}

export interface IColumnConfig {
  id: string;
  name: string;
  key: string;
  color: string;
  order: number;
  isActive: boolean;
  /** Ao entrar nesta coluna, pede uma observação antes de efetivar a mudança de situação. */
  requireObservationOnEnter?: boolean;
  /** Só tem efeito quando `requireObservationOnEnter` é true — torna o texto obrigatório. */
  observationRequiredOnEnter?: boolean;
  /** Só relevante na coluna 'in_progress' — ao mover uma demanda pra cá, oferece encaminhar a um
   * prestador antes de efetivar. Default true nela (preserva o comportamento hoje fixo). */
  showProviderForwardOnEnter?: boolean;
  /** Ao selecionar esta coluna no formulário de edição, exibe o campo de data de conclusão
   * pré-preenchido com a data atual, editável. Se a demanda já tiver completedAt preenchido, o
   * campo aparece de qualquer forma. Default false (mantém o preenchimento automático de hoje). */
  showCompletedAtOnEnter?: boolean;
}

export interface IPriorityConfig {
  id: string;
  name: string;
  key: string;
  color: string;
  order: number;
  isActive: boolean;
}

export interface ICategoryConfig {
  id: string;
  name: string;
  key: string;
  color: string;
  order: number; isActive: boolean;
}

/** Config de um campo no formulário público (visibilidade e obrigatoriedade por tipo de solicitante) */
export interface IApplicantTypeFormFieldConfig {
  visible: boolean;
  required: boolean;
}

/** Campo wantsEmailUpdates: só visível (checkbox), sem obrigatoriedade. requesterEmail usa required quando o campo é exibido. */
export type ApplicantTypeFormFieldsConfig = Partial<{
  applicantType: IApplicantTypeFormFieldConfig;
  requesterName: IApplicantTypeFormFieldConfig;
  requesterPhone: IApplicantTypeFormFieldConfig;
  block: IApplicantTypeFormFieldConfig;
  unitNumber: IApplicantTypeFormFieldConfig;
  wantsEmailUpdates: { visible: boolean }; // checkbox - sem required
  requesterEmail: { required: boolean };   // visível só quando wantsEmailUpdates marcado
  category: IApplicantTypeFormFieldConfig;
  description: IApplicantTypeFormFieldConfig;
}>;

/** Tipo de solicitante (ex.: Morador, Zelador). Morador sempre existe e não pode ser editado/excluído/inativado. */
export interface IApplicantTypeConfig {
  id: string;
  name: string;
  key: string;
  order: number;
  isActive: boolean;
  /** Configuração de campos do formulário público: visível e obrigatório por tipo */
  formFields?: ApplicantTypeFormFieldsConfig;
}

// Configurações específicas para demandas
export interface IDemandsConfig {
  columns: IColumnConfig[];
  priorities: IPriorityConfig[];
  categories: ICategoryConfig[];
  applicantTypes?: IApplicantTypeConfig[];
  defaultPeriod: 'all' | 'today' | 'week' | 'month' | 'year';
  // Configuração do cron job de recorrências
  recurringDemandsCronSchedule?: string; // Formato cron: "0 2 * * *" (minuto hora dia mês dia-da-semana)
  recurringDemandsCronTimezone?: string; // Timezone: "America/Sao_Paulo"
  recurringDemandsCronConfig?: CronScheduleConfig; // Nova configuração completa de cron
  recurringDemandsCronEnabled?: boolean; // true = habilitado, false = desabilitado
  // Configuração do cron job de lembrete de demanda recorrente (1 dia antes + no dia da data
  // prevista) — mesmo mecanismo acima, schedule independente do cron de geração.
  recurringDemandReminderCronSchedule?: string;
  recurringDemandReminderCronTimezone?: string;
  recurringDemandReminderCronConfig?: CronScheduleConfig;
  recurringDemandReminderCronEnabled?: boolean;
  autoAssignProviderEnabled?: boolean; autoAssignTieBreakStrategy?: 'first' | 'rotate'; // Fase 3a
}

// Configurações gerais do sistema
export interface CronScheduleConfig {
  mode: 'simple' | 'advanced';
  simple?: {
    hour: number; // 0-23
    minute: number; // 0-59
    daysOfWeek?: number[]; // 0-6 (0 = domingo), se não especificado = todos os dias
  };
  advanced?: {
    minutes?: number | number[] | '*'; // 0-59
    hours?: number | number[] | '*'; // 0-23
    dayOfMonth?: number | number[] | '*'; // 1-31
    month?: number | number[] | '*'; // 1-12
    dayOfWeek?: number | number[] | '*'; // 0-6 (0 = domingo)
    customExpression?: string; // Expressão cron completa
  };
}

export interface IGeneralConfig {
  systemName?: string;
  systemVersion?: string;
  openaiApiKey?: string; // API Key do OpenAI (não retornada por segurança)
  geminiApiKey?: string; // API Key do Gemini (não retornada por segurança)
  aiProvider?: 'openai' | 'gemini'; // Provedor de IA escolhido para os recursos configuráveis
  hasOpenaiApiKey?: boolean; // true se o tenant tem uma chave própria de OpenAI salva (calculado no backend, nunca a chave em si)
  hasGeminiApiKey?: boolean; // true se o tenant tem uma chave própria de Gemini salva (calculado no backend, nunca a chave em si)
}

// Configurações de e-mail
export interface IEmailConfig {
  enabled: boolean;
  sender: {
    name: string;
  };
  /** Endereço para onde as respostas devem ir (ex.: email do síndico). Não precisa ser do mesmo domínio. */
  replyTo?: string;
  footerText?: string;
  signature?: string;
}

// Configurações de relatórios
export interface IPerformanceThresholds {
  excellent: number;  // < excellent dias = Excelente
  good: number;        // >= excellent e < good dias = Bom
  regular: number;     // >= good e < regular dias = Regular
  // > regular dias = Crítico
}

export interface ISeverityThresholds {
  critical: number;   // >= critical ocorrências = CRÍTICO
  attention: number;  // >= attention e < critical = ATENÇÃO
  monitor: number;    // >= monitor e < attention = MONITORAR
  criticalColor?: string;  // Cor para CRÍTICO (ex: '#f44336')
  attentionColor?: string; // Cor para ATENÇÃO (ex: '#ff9800')
  monitorColor?: string;   // Cor para MONITORAR (ex: '#2196f3')
}

export interface IReportsConfig {
  defaultPeriod: 'all' | 'today' | 'week' | 'month' | 'year';
  exportFormats: ('pdf' | 'excel' | 'csv')[];
  performanceThresholds: IPerformanceThresholds;
  severityThresholds: ISeverityThresholds;
}

// Configuração completa do sistema
export interface ISystemConfig {
  general: IGeneralConfig;
  demandManagement: IDemandsConfig;
  reports: IReportsConfig;
  email?: IEmailConfig;
  updatedAt: Date;
  version: string;
}

// Demandas Recorrentes
export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'semiannual' | 'annual';

export interface IRecurringDemand {
  _id: string;
  clientId: string;
  description: string;
  category: string;
  priority: string;
  frequency: RecurrenceFrequency;
  dayOfMonth?: number;
  dayOfWeek?: number;
  startDate: Date | string;
  endDate?: Date | string;
  lastGeneratedDate?: Date | string;
  nextDueDate: Date | string;
  autoAssign?: string;
  estimatedHours?: number;
  /** Opcionais, copiados para cada ocorrência — Fases 2 e 4, ver PLANO_CHECKLIST_QRCODE_PATRIMONIO. */
  qrCodeId?: string;
  checklistId?: string;
  patrimonyListId?: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

/** Tipo de identificação do item no formulário público (numérico, apenas letras ou alfanumérico) */
export type ItemIdentificationType = 'numeric' | 'alphabetic' | 'alphanumeric';

export interface IQRCode {
  _id: string;
  clientId: string;
  identifier: string; // Identificador único do QR code (ex: 'academia', 'salao-gourmet')
  description: string; // Descrição para facilitar identificação (ex: 'QR Code da Academia')
  /** Se true, o formulário público exige que o usuário informe a identificação do item (ex.: número do poste) */
  requiresItemIdentification?: boolean;
  /** Tipo de identificação: numeric, alphabetic ou alphanumeric */
  itemIdentificationType?: ItemIdentificationType;
  publicToken: string; // Token público único e não previsível para acesso seguro via URL
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

/** Exige evidência fotográfica pré e/ou pós-serviço para concluir a tarefa. Os dois flags são
 * independentes (uma tarefa pode exigir só pré, só pós, ambas, ou nenhuma). */
export interface IChecklistItemEvidenceRequired {
  pre: boolean;
  post: boolean;
}

export interface IChecklistItem {
  _id: string;
  description: string;
  order: number;
  evidenceRequired: IChecklistItemEvidenceRequired;
}

/** Cadastro administrativo reutilizável de checklist — o "molde" selecionável em demandas
 * avulsas/recorrentes (Fase 2). Alterar um checklist aqui nunca afeta execuções já iniciadas
 * (que guardam seu próprio snapshot). */
export interface IChecklist {
  _id: string;
  clientId: string;
  name: string;
  isActive: boolean;
  items: IChecklistItem[];
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface IPatrimonyListItem {
  _id: string;
  name: string;
  /** Referência estável — "quanto deveria ter". Só muda por ação explícita do gestor (ver
   * "Ajustar referência" em PatrimonyItemEditor.tsx); não é mais sobrescrita a cada conferência
   * (decisão revertida em 05/09, ver PatrimonyList.ts no backend). */
  expectedQuantity: number;
  /** Última quantidade encontrada numa conferência via QR code — `null`/ausente = nunca
   * conferido. Divergência = preenchido e diferente de `expectedQuantity`. */
  lastFoundQuantity?: number | null;
  lastFoundAt?: string | null;
  /** Condição registrada na última conferência (valor de PATRIMONY_CONDITIONS no backend:
   * 'normal'/'danificado'/'ausente'/'divergencia_quantidade') — `null`/ausente = nunca conferido. */
  lastCondition?: string | null;
  /** Nome de quem fez a última conferência (login próprio ou nome digitado, papel de login
   * compartilhado/QR-only) — já resolvido em texto pelo backend, sem precisar populate. */
  lastInspectedByName?: string | null;
  /** Referência (inspeção + item snapshot) da última conferência — usada só para buscar a
   * evidência fotográfica sob demanda (`apiService.listPatrimonyItemEvidence`), sem duplicar as
   * imagens aqui. `null`/ausente = nunca conferido ou conferido sem foto. */
  lastInspectionId?: string | null;
  lastInspectionItemId?: string | null;
  /** Permite anexar evidência fotográfica (até 2) neste item — pedido do usuário (09/09):
   * "possibilitando marcar se permitirá inserir evidência e/ou se será obrigatório". Mestre de
   * `evidenceRequired`: sem isto, a captura nunca aparece. Só oferecida na conferência quando a
   * condição selecionada é "Danificado" — as demais não fazem sentido terem foto. */
  evidenceEnabled?: boolean;
  /** Exige pelo menos 1 evidência fotográfica para inspecionar este item como "Danificado" — só
   * tem efeito quando `evidenceEnabled` também está marcado. */
  evidenceRequired?: boolean;
}

/** Cadastro administrativo reutilizável de lista de patrimônio (Fase 4) — mesmo papel de
 * IChecklist, mas para a inspeção de itens físicos (mobiliário, equipamentos etc.) de um QR code. */
export interface IPatrimonyList {
  _id: string;
  clientId: string;
  name: string;
  isActive: boolean;
  items: IPatrimonyListItem[];
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

/** Evidência fotográfica de uma tarefa de checklist (Fase 3) — coleção própria
 * (DemandEvidenceAttachment), com fase pré/pós-serviço. */
export interface IChecklistTaskEvidence {
  _id: string;
  phase: 'pre' | 'post';
  imageBase64: string;
  contentType?: string;
  createdAt?: Date | string;
}

export interface IChecklistExecutionTask {
  _id: string;
  descriptionSnapshot: string;
  order: number;
  evidenceRequiredSnapshot: IChecklistItemEvidenceRequired;
  status: 'pendente' | 'concluida';
  completedAt?: Date | string;
  completedBy?: string;
}

/** Execução de um checklist numa ocorrência de demanda (Fases 2 e 3) — snapshot imutável do
 * checklist no momento da criação, para exibir histórico/evidências ao gestor
 * (DemandDetails.tsx). */
export interface IChecklistExecution {
  _id: string;
  demandId: string;
  checklistNameSnapshot: string;
  status: 'em_andamento' | 'concluida' | 'nao_realizada';
  startedAt?: Date | string;
  completedAt?: Date | string;
  tasks: IChecklistExecutionTask[];
}

/** Evidência fotográfica de um item de patrimônio (Fase 4) — coleção própria
 * (PatrimonyInspectionAttachment), sem a semântica pré/pós do checklist. */
export interface IPatrimonyItemEvidence {
  _id: string;
  imageBase64: string;
  contentType?: string;
  createdAt?: Date | string;
}

export interface IPatrimonyInspectionItem {
  _id: string;
  nameSnapshot: string;
  expectedQuantity: number;
  /** Snapshot de IPatrimonyListItem.evidenceEnabled/evidenceRequired no momento da criação da
   * inspeção. A captura só é oferecida quando `evidenceEnabledSnapshot` E a condição selecionada
   * na conferência é "danificado". */
  evidenceEnabledSnapshot?: boolean;
  evidenceRequiredSnapshot?: boolean;
  foundQuantity?: number;
  condition?: string;
  observation?: string;
  status: 'pendente' | 'inspecionado';
  inspectedAt?: Date | string;
  inspectedBy?: string;
  /** Referência à demanda já aberta a partir deste item (populado com demandNumber, mesmo padrão
   * do checklist) — presente quando alguém já usou "Abrir demanda para este item". */
  reportedDemandId?: { _id: string; demandNumber: string } | string;
}

/** Inspeção de uma lista de patrimônio numa ocorrência de demanda (Fase 4) — mesmo papel de
 * IChecklistExecution, para inspeção de itens físicos em vez de tarefas. */
export interface IPatrimonyInspection {
  _id: string;
  /** Ausente numa conferência avulsa (pedido do usuário 08/09: sem Demand) — presente só no fluxo
   * QR code/demanda. */
  demandId?: string;
  patrimonyListNameSnapshot: string;
  status: 'em_andamento' | 'concluida' | 'nao_realizada';
  startedAt?: Date | string;
  completedAt?: Date | string;
  items: IPatrimonyInspectionItem[];
}
